// src/pages/MsoRulesPage.tsx
//
// ★★ ANNEX A 22 — the MSO pro-ration rules: one row per rule, its version in force and any open
// draft; each expands to the whole version timeline, and any two versions compare side by side.
//
// ══ WHO MAY SEE THIS ══
//
// PSD only. Every route behind the screen answers 403 to anyone else, and the navigation entry is
// gated on the same predicate (`mayAdministerReferenceData`) the route calls.
//
// ⚠ A rule written here prices every MSO bill it claims, so this screen states the boundary once,
// up front, and the writes it offers are the REVERSIBLE ones (a draft). Activating and retiring,
// which move money, are on the editor behind a confirmation.
//
// ⚠ The list is COMPLETE in the browser (one row per rule, a handful in all), so it sorts
// client-side; there is no pager to disagree with.
import { useState } from 'react'
import { Link } from 'react-router'
import { IconButton } from '../components/IconButton'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useClientSort, type ClientSortColumns } from '../lib/useClientSort'
import { formatIsoDate } from '../lib/format'
import { ExportToolbar } from '../components/ExportToolbar'
import { startDownload } from '../lib/start-download'
import { MsoAuditEvidence } from '../components/MsoAuditEvidence'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { MsoNewDraftPanel, type NewDraftMode } from '../components/MsoNewDraftPanel'
import { MsoStatusBadge, MsoVersionTimeline } from '../components/MsoVersionTimeline'
import type { MsoRuleSummary } from '../lib/types'

type RuleSortKey = 'ruleKey' | 'name' | 'activeVersion' | 'effectiveFrom' | 'precedence' | 'draft'

const COLUMN_LABELS: Readonly<Record<RuleSortKey, string>> = {
  ruleKey: 'Rule',
  name: 'Name',
  activeVersion: 'Version in force',
  effectiveFrom: 'Effective from',
  precedence: 'Precedence',
  draft: 'Open draft',
}

const COLUMNS: ClientSortColumns<MsoRuleSummary, RuleSortKey> = {
  ruleKey: { type: 'text', valueOf: (r) => r.ruleKey },
  name: { type: 'text', valueOf: (r) => r.name },
  activeVersion: { type: 'number', valueOf: (r) => r.active?.version ?? null },
  effectiveFrom: { type: 'date', valueOf: (r) => r.active?.effectiveFrom ?? null },
  precedence: { type: 'number', valueOf: (r) => r.active?.definition.precedence ?? null },
  draft: { type: 'number', valueOf: (r) => r.draft?.version ?? null },
}

export function MsoRulesPage() {
  const rules = useApi(() => api.msoRules(), [])
  const all = rules.data?.rules ?? []
  const { rows, sort, onSort, interacted } = useClientSort(all, COLUMNS)
  const [open, setOpen] = useState<string | null>(null)
  const [authoring, setAuthoring] = useState<NewDraftMode | null>(null)
  // N-11: a refusal (403) means this officer cannot use the screen, so none of its controls is offered beside the message
  const refused = rules.errorStatus === 403

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl mb-2">MSO pro-ration rules</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            The rules that divide an MSO bill between the officer or dependant and Government. Each rule is
            versioned and effective-dated; a calculation is priced by the version in force on its date of treatment. PSD only.
          </p>
        </div>
        {!refused && (
        <div className="pt-no-print flex flex-col items-end gap-3">
          <SgdsButton type="button" variant="primary" onClick={() => setAuthoring({ kind: 'scenario' })}>
            New MSO scenario
          </SgdsButton>
          <ExportToolbar
            onPrint={() => window.print()}
            onDownload={(format) => {
              startDownload(api.msoRulesExportUrl(format))
            }}
          />
        </div>
        )}
      </div>

      <div className="pt-unbuilt">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">How a rule reaches a bill</p>
        <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
          A rule written here prices every MSO bill it claims, so a change is a policy decision. A new version is a{' '}
          <strong>draft</strong> until it has passed its test cases and a <strong>second PSD officer</strong> has activated
          it. The officer who created or last edited a draft cannot activate it.
        </p>
      </div>

      {authoring !== null && <MsoNewDraftPanel mode={authoring} rules={all} bounds={rules.data?.bounds} onClose={() => setAuthoring(null)} />}

      <SortAnnouncement sort={sort} interacted={interacted} busy={rules.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {rules.error !== null ? (
        <ErrorCard title="Could not load the MSO rules" message={rules.error} {...(rules.retryable && { onRetry: rules.reload })} />
      ) : rules.initialLoading ? (
        <LoadingCard label="Loading the MSO rules" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No MSO rule exists"
          message="The three standard rules are seeded by migration, so an empty list means this database has not been migrated, or your active identity is not PSD."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={rules.loading}>
            <table className="pt-table">
              <caption className="sr-only">MSO pro-ration rules, one row per rule</caption>
              <thead>
                <tr>
                  <SortableTh label="Rule" sortKey="ruleKey" sort={sort} onSort={onSort} />
                  <SortableTh label="Name" sortKey="name" sort={sort} onSort={onSort} />
                  <SortableTh label="Version in force" sortKey="activeVersion" sort={sort} onSort={onSort} />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                  <SortableTh label="Precedence" sortKey="precedence" sort={sort} onSort={onSort} align="right" />
                  <SortableTh label="Open draft" sortKey="draft" sort={sort} onSort={onSort} />
                  <th scope="col" className="pt-no-print" data-unsortable="one set of controls per row, not data, so a sortable header over buttons would claim to order something with no order">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((rule) => (
                  <RuleRow
                    key={rule.ruleKey}
                    rule={rule}
                    expanded={open === rule.ruleKey}
                    onToggle={() => setOpen(open === rule.ruleKey ? null : rule.ruleKey)}
                    onNewDraft={() => setAuthoring({ kind: 'version', rule })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <MsoAuditEvidence
        heading="Recent MSO audit events"
        intro="The 20 most recent audit events across the MSO rule engine: rule drafts, edits, test runs and activations, and calculations."
        scopes={[
          { resourceType: 'mso-rule-version', linkLabel: 'Open rule events in the audit trail' },
          { resourceType: 'mso-calculation', linkLabel: 'Open calculation events in the audit trail' },
        ]}
      />
    </div>
  )
}

function RuleRow({
  rule,
  expanded,
  onToggle,
  onNewDraft,
}: {
  rule: MsoRuleSummary
  expanded: boolean
  onToggle: () => void
  onNewDraft: () => void
}) {
  return (
    <>
      <tr>
        <td className="font-semibold pt-mono whitespace-nowrap">{rule.ruleKey}</td>
        <td className="min-w-[11rem] max-w-[32rem] [overflow-wrap:anywhere]">
          <span className="text-sm [overflow-wrap:anywhere]">{rule.name}</span>
          {/* Capped at two lines so one long purpose cannot push the row actions out of view (UAT F-07, F-19); the
              whole text is in the tooltip and in the versions panel below the row. */}
          <span className="pt-clamp-2 text-xs text-[var(--sgds-body-color-subtle)]" title={rule.purpose}>
            {rule.purpose}
          </span>
        </td>
        <td>
          {rule.active === null ? (
            <span className="pt-mono">None in force</span>
          ) : (
            <span className="inline-flex flex-wrap items-center gap-2">
              <span className="pt-mono">{`v${rule.active.version}`}</span>
              <MsoStatusBadge status="ACTIVE" />
            </span>
          )}
        </td>
        <td className="text-sm">{rule.active === null ? 'Not applicable' : formatIsoDate(rule.active.effectiveFrom)}</td>
        <td className="pt-num pt-tabular">{rule.active === null ? 'None' : rule.active.definition.precedence}</td>
        <td>
          {rule.draft === null ? (
            <span className="text-sm text-[var(--sgds-body-color-subtle)]">None</span>
          ) : (
            <Link to={`/administration/mso-rules/versions/${rule.draft.id}`}>Draft v{rule.draft.version}</Link>
          )}
        </td>
        <td className="pt-no-print">
          {/* Two icons, named in full by their tooltips and hidden text: the long word buttons were wider than the
              column and were being clipped at the table edge, which squeezed the Name column beside them. */}
          <div className="flex gap-2 whitespace-nowrap">
            <IconButton
              glyph="history"
              label={expanded ? `Hide versions of ${rule.ruleKey}` : `Versions of ${rule.ruleKey}`}
              aria-expanded={expanded}
              onClick={onToggle}
            />
            {rule.draft === null && <IconButton glyph="plus" label={`New draft of ${rule.ruleKey}`} onClick={onNewDraft} />}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7}>
            <p className="text-sm mb-3 max-w-[75ch] [overflow-wrap:anywhere]">
              <span className="font-semibold">Purpose: </span>
              {rule.purpose}
            </p>
            <MsoVersionTimeline ruleKey={rule.ruleKey} />
          </td>
        </tr>
      )}
    </>
  )
}
